import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  HelpCircle,
  MessageCircle,
  Mail,
  Phone,
  Search,
  Rocket,
  BookOpen,
  RotateCcw,
} from 'lucide-react';
import { useState } from 'react';
import { useTour } from '../contexts/TourContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useIsCustomer } from '../hooks/usePortalMode';

type FaqSlug =
  | 'getting_started'
  | 'purchasing'
  | 'customer_management'
  | 'documents_communication';
type FaqAudience = 'supplier' | 'customer' | 'both';
interface FaqItem { question: string; answer: string; audience: FaqAudience }
interface FaqCategory {
  slug: FaqSlug;
  category: string;
  /** When set, the whole category is hidden from the other audience */
  categoryAudience?: Exclude<FaqAudience, 'both'>;
  items: FaqItem[];
}

// Stable slugs match backend PlatformSettings.help_layout.faq keys so a
// superuser can hide any category for a tenant whose workflow doesn't need it.
// Items and categories are tagged by audience so buyer-company users only see
// content relevant to them; operating-org staff see supplier-facing content.
const FAQ_ITEMS: FaqCategory[] = [
  {
    slug: 'getting_started',
    category: 'Getting Started',
    items: [
      {
        audience: 'supplier',
        question: 'How do I navigate the platform?',
        answer: 'Use the sidebar on the left to open each module. The Dashboard shows delivery and order metrics across your customer base; click any chart to drill into the underlying records.',
      },
      {
        audience: 'customer',
        question: 'How do I navigate the platform?',
        answer: 'Use the sidebar on the left to open the modules your supplier has enabled for you — typically Dashboard, Sales Orders, Quality, Documents and News. Click any menu item to navigate; the sidebar can be collapsed with the arrow button.',
      },
      {
        audience: 'supplier',
        question: 'How do I use the Dashboard?',
        answer: 'The Dashboard summarises sales order and delivery performance across your customer companies. Click any KPI or chart to drill into the underlying orders or deliveries.',
      },
      {
        audience: 'customer',
        question: 'How do I use the Dashboard?',
        answer: 'Your Dashboard shows open sales orders, delivery status and quality metrics for your company. Click any chart or KPI to see the underlying records.',
      },
      {
        audience: 'both',
        question: 'How do I update my profile information?',
        answer: 'Open the Settings page from the user menu in the top-right. In the Profile tab, update your name, contact details and notification preferences.',
      },
      {
        audience: 'both',
        question: 'How do I change my password?',
        answer: 'Open Settings > Security and click "Change Password". You will need to enter your current password to set a new one.',
      },
      {
        audience: 'customer',
        question: 'How do I view quality issues?',
        answer: 'Open Quality from the sidebar to see Non-Conformance Reports (NCRs) and concessions raised against your company. Open a record to review details and submit your response.',
      },
    ],
  },
  {
    slug: 'purchasing',
    category: 'Sales Orders',
    items: [
      {
        audience: 'customer',
        question: 'How do I view my sales orders?',
        answer: 'Open Sales Orders from the sidebar. You see orders your supplier has issued to you, along with their lines, schedules and delivery status. Use the filters to narrow by status, date range or part number.',
      },
      {
        audience: 'customer',
        question: 'How do I acknowledge a sales order?',
        answer: 'Open the order from the SO Lines tab and click Acknowledge. This confirms you have accepted the order and agree to the quantities, dates and price.',
      },
      {
        audience: 'customer',
        question: 'How do I propose changes to a delivery date, quantity, or price?',
        answer: 'In the SO Lines view, open the line you want to change and click Propose Change. Enter your proposed value and a reason — your supplier is notified and can accept, reject, or counter-propose.',
      },
      {
        audience: 'customer',
        question: 'How do I track incoming deliveries?',
        answer: 'Your supplier notifies you of shipments via Advanced Shipping Notices (ASNs). Open Sales Orders > ASN to see planned and completed deliveries against your orders.',
      },
      {
        audience: 'supplier',
        question: 'Where do I manage sales orders I have issued?',
        answer: 'Open Sales Orders from the sidebar. The SO Headers and SO Lines tabs list everything you have issued to customer companies. Open any order to see schedules, acknowledgements and delivery status.',
      },
      {
        audience: 'supplier',
        question: 'How do I notify a customer of a shipment?',
        answer: 'Create an Advanced Shipping Notice (ASN) from Sales Orders > ASN. Record the delivery date and quantities shipped — ASNs feed the On-Time Delivery (OTD) metrics on customer profiles.',
      },
    ],
  },
  {
    slug: 'customer_management',
    category: 'Customer Management',
    categoryAudience: 'supplier',
    items: [
      {
        audience: 'supplier',
        question: 'How do I add a new customer company?',
        answer: 'Open Customers and use Add. Enter the company name, contact details and any required profile information to create the customer record.',
      },
      {
        audience: 'supplier',
        question: 'How do I view customer delivery performance?',
        answer: `Open the customer's profile and switch to the Performance tab. OTD appears here once deliveries and ASNs have been recorded against the customer. Configure OTD range thresholds in Admin Setup > Performance.`,
      },
    ],
  },
  {
    slug: 'documents_communication',
    category: 'Documents & Communication',
    items: [
      {
        audience: 'supplier',
        question: 'How do I upload a document?',
        answer: 'Open Documents and click Upload Document. Choose whether to keep it private to your organisation, share it with a specific customer company, or mark it public. Every download is tracked on the document\'s read-receipt log.',
      },
      {
        audience: 'customer',
        question: 'Which documents can I see?',
        answer: 'You can see documents you have uploaded, documents scoped to your own company, and any documents your supplier has marked public. You cannot see another customer company\'s documents.',
      },
      {
        audience: 'customer',
        question: 'How do I upload a document my supplier needs?',
        answer: 'Use the Documents page to upload the file. Your supplier is notified once it appears under your company profile.',
      },
      {
        audience: 'customer',
        question: 'How do I read news and announcements?',
        answer: 'Open News from the sidebar (or check your Dashboard) to see announcements your supplier has published for customer users. Unread items are highlighted until you open them.',
      },
      {
        audience: 'supplier',
        question: 'How do I create a news announcement?',
        answer: 'Open News & Announcements from the sidebar (admin only) and click Create News. Pick an audience (customer users, internal staff, or both), write the content and publish — the targeted users see the announcement on their dashboard.',
      },
    ],
  },
];

export default function Help() {
  const [searchTerm, setSearchTerm] = useState('');
  const { startTour, setShowQuickReference, hasSeenTour, resetTour } = useTour();
  const { helpLayout } = useMenuSettings();
  const isCustomer = useIsCustomer();
  const audience: Exclude<FaqAudience, 'both'> = isCustomer ? 'customer' : 'supplier';

  const filteredFAQ = FAQ_ITEMS
    .filter(category => helpLayout.faq[category.slug] !== false)
    .filter(category => !category.categoryAudience || category.categoryAudience === audience)
    .map(category => ({
      ...category,
      items: category.items
        .filter(item => item.audience === 'both' || item.audience === audience)
        .filter(
          item =>
            item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.answer.toLowerCase().includes(searchTerm.toLowerCase())
        ),
    }))
    .filter(category => category.items.length > 0);

  const showTour = helpLayout.sidebar.tour !== false;
  const showContact = helpLayout.sidebar.contact_support !== false;
  const showStatus = helpLayout.sidebar.system_status !== false;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Help & Support</h1>
        <p className="text-muted-foreground mt-2">
          {isCustomer
            ? 'Find answers about your sales orders, deliveries, documents and how to use the portal.'
            : 'Find answers about sales orders, deliveries, customer management and platform navigation.'}
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* FAQ Section */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Frequently Asked Questions
              </CardTitle>
              <CardDescription>
                {isCustomer
                  ? 'Common questions for buyer users'
                  : 'Browse common questions organized by topic'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredFAQ.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No results found for "{searchTerm}"
                </p>
              ) : (
                <div className="space-y-6">
                  {filteredFAQ.map((category) => (
                    <div key={category.category}>
                      <h3 className="font-semibold text-lg mb-3 text-primary">
                        {category.category}
                      </h3>
                      <Accordion type="single" collapsible className="w-full">
                        {category.items.map((item, index) => (
                          <AccordionItem key={index} value={`${category.category}-${index}`}>
                            <AccordionTrigger className="text-left">
                              {item.question}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground">
                              {item.answer}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* Contact Support Sidebar */}
        <div className="space-y-4">
          {/* New to Helix Card */}
          {showTour && (
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-primary" />
                {isCustomer ? 'New to the portal?' : 'New to Helix?'}
              </CardTitle>
              <CardDescription>
                {isCustomer
                  ? 'Take a guided tour of the areas available to you'
                  : 'Take a tour or view the navigation guide'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={startTour} className="w-full">
                <Rocket className="h-4 w-4 mr-2" />
                Start Interactive Tour
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowQuickReference(true)}
                className="w-full"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Quick Reference Guide
              </Button>
              {hasSeenTour && (
                <Button
                  variant="ghost"
                  onClick={resetTour}
                  className="w-full text-muted-foreground"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset Tour
                </Button>
              )}
            </CardContent>
          </Card>
          )}

          {showContact && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Contact Support
              </CardTitle>
              <CardDescription>
                {isCustomer
                  ? 'Get help from your supplier or platform support'
                  : 'Get in touch with our support team'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {isCustomer ? 'Platform Support' : 'Email Support'}
                    </p>
                    <a
                      href="mailto:support@valuechain.com"
                      className="text-sm text-primary hover:underline"
                    >
                      support@valuechain.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Phone Support</p>
                    <p className="text-sm text-muted-foreground">
                      {isCustomer
                        ? 'Contact your supplier account manager for order or delivery queries'
                        : 'Contact your account manager'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-3">
                  Support Hours: Monday - Friday, 9am - 5pm GMT
                </p>
                <a href="mailto:support@valuechain.com" className="w-full">
                  <Button className="w-full">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Email Support
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
          )}

          {showStatus && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">System Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-green-500" />
                <span className="text-sm font-medium">All Systems Operational</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Last updated: {new Date().toLocaleString()}
              </p>
            </CardContent>
          </Card>
          )}

        </div>
      </div>
    </div>
  );
}
